import { useFocusedByKeyboard } from './useFocusedByKeyboard';

/**
 * Whether Enter or Space belongs to the focused control rather than to image navigation,
 * in an object session.
 *
 * A key that presses a focused button must not also move the image: the browser clicks it
 * on Enter and Space, so Train New Object (focused again when its modal closes) would
 * reopen while the image also moved. A button the keyboard reached keeps the key. One that
 * only holds focus from a mouse click does not: the key moves the image and the browser's
 * click is held back, so Save clicked and then Enter goes on rather than saving again. A
 * `role="button"` acts in its own handler, before this one, so it always keeps the key.
 * So does a control the keyboard reached that handled the key and said so: a label chip
 * toggles on Space. A chip that only holds focus from a click re-picks its own label on
 * Space, which changes nothing, so the key moves the image as it does from a clicked button.
 * Buttons only otherwise: a focused radio of the tool picker does nothing on Enter.
 *
 * @param {boolean} enabled - an object session; otherwise no key belongs to a control
 * @returns {(event: KeyboardEvent) => boolean} true when the navigation must leave the key
 *   alone; when it returns false for a clicked button, it has held back the browser's click
 */
export const useKeyBelongsToControl = enabled => {
	const focusedByKeyboard = useFocusedByKeyboard(enabled);

	return event => {
		if (!enabled) return false;
		const focused = document.activeElement;
		if (event.defaultPrevented && focused && focusedByKeyboard(focused)) return true;
		const control = focused?.closest('button, [role="button"]');
		if (!control) return false;
		if (control.tagName !== 'BUTTON' || focusedByKeyboard(control)) return true;
		event.preventDefault();
		return false;
	};
};
